/**
 * @providesModule cardsById
 * 
 */

import { EditorState, convertFromRaw } from 'draft-js'
import { decorator } from 'draft/config'
import produce from 'immer'

import {
  complement,
  isNil,
  filter,
  where,
  omit,
  lensPath,
  view,
  set,
  reduce,
  values,
} from 'ramda'



const { forceSelection } = EditorState


function cardsById (
  state = ({ ...window.caseData.cards }),
  action
) {
  switch (action.type) {
    case 'SET_CARDS':
      return {
        ...state,
        ...action.cards,
      }

    case 'UPDATE_CARD_CONTENTS':
      return {
        ...state,
        [action.id]: {
          ...state[action.id],
          editorState: action.editorState,
        },
      }

    case 'APPLY_SELECTION': {
      const editorState =
        state[action.cardId].editorState || EditorState.createEmpty()

      return {
        ...state,
        [action.cardId]: {
          ...state[action.cardId],
          editorState: forceSelection(editorState, action.selectionState),
        },
      }
    }

    case 'REPLACE_CARD': {
      const { cardId, newCard } = action
      return produce(state, draft => {
        draft = updatePositions(draft, cardId, newCard.position)
        draft[cardId] = newCard
        draft[cardId].editorState = parseEditorStateFromPersistedCard(newCard)
      })
    }

    case 'REORDER_CARD': {
      const { id, destination } = action

      return updatePositions(state, id, destination + 1)
    }

    case 'REMOVE_CARD':
      return omit([action.id], state)

    case 'ADD_CARD':
      return {
        ...state,
        [action.data.id]: {
          ...action.data,
          editorState: parseEditorStateFromPersistedCard(action.data),
        },
      }

    case 'PARSE_ALL_CARDS':
      return Object.keys(state)
        .map(key => state[key])
        .reduce(
          (all, card) => ({
            ...all,
            [card.id]: {
              ...card,
              commentThreads:
                card.commentThreads &&
                card.commentThreads.sort(sortCommentThreads),
              editorState: parseEditorStateFromPersistedCard(card),
            },
          }),
          {}
        )

    case 'ADD_COMMENT_THREAD': {
      const { data } = action
      const { cardId } = data
      if (cardId == null) return state

      const card = state[cardId]
      const commentThreads = card.commentThreads || []

      if (commentThreads.find(x => x.id === data.id)) return state

      const cardWithThread = {
        ...card,
        commentThreads: [...commentThreads, data].sort(sortCommentThreads),
      }
      return {
        ...state,
        [cardId]: {
          ...cardWithThread,
          editorState: parseEditorStateFromPersistedCard(cardWithThread),
        },
      }
    }

    case 'REMOVE_COMMENT_THREAD': {
      const { cardId, threadId } = action
      if (cardId == null) return state

      const card = state[cardId]
      const commentThreads = card.commentThreads || []

      const cardWithoutThread = {
        ...card,
        commentThreads: commentThreads.filter(
          thread => `${thread.id}` !== threadId
        ),
      }

      return {
        ...state,
        [cardId]: {
          ...cardWithoutThread,
          editorState: parseEditorStateFromPersistedCard(cardWithoutThread),
        },
      }
    }

    case 'ADD_PODCAST':
      return {
        ...state,
        [action.data.cardId]: {
          solid: true,
          editorState: EditorState.createEmpty(),
        },
      }

    default:
      return state
  }
}

export default cardsById

// Comment Threads that are attached to a card should be displayed in the order
// that their highlighted text appears on the card. Threads that have had their
// text changed out from under them should appear last.
function sortCommentThreads (
  a,
  b
) {
  if (a.start == null || a.blockIndex == null) return 1
  if (b.start == null || b.blockIndex == null) return -1

  if (a.blockIndex !== b.blockIndex) return a.blockIndex - b.blockIndex
  return a.start - b.start
}

function parseEditorStateFromPersistedCard (card) {
  const content = card.rawContent
  if (content == null) return EditorState.createEmpty(decorator)

  const contentWithCommentThreads = addCommentThreads(content, card)

  const contentState = convertFromRaw(contentWithCommentThreads)

  return EditorState.createWithContent(contentState, decorator)
}

function addCommentThreads (content, card) {
  const commentThreads = card.commentThreads || []

  const styleRangesForComment = ({ id, length, start: offset }) => [
    { length, offset, style: 'THREAD' },
    { length, offset, style: `thread--${id}` },
  ]

  const inlineStylesLensForBlock = blockIndex =>
    lensPath(['blocks', blockIndex, 'inlineStyleRanges'])

  const inlineStylesWithComment = (content, comment) => [
    ...view(inlineStylesLensForBlock(comment.blockIndex), content),
    ...styleRangesForComment(comment),
  ]

  const setInlineStylesForComment = (content, comment) =>
    set(
      inlineStylesLensForBlock(comment.blockIndex),
      inlineStylesWithComment(content, comment),
      content
    )

  const attached = filter(
    where({ blockIndex: complement(isNil), start: complement(isNil) })
  )

  return reduce(setInlineStylesForComment, content, attached(commentThreads))
}

// NOTE: position is 1-indexed!
function updatePositions (state, id, destination) {
  return produce(state, draft => {
    const card = draft[id]
    const source = card.position

    const otherCardsOnPage = values(draft).filter(c => {
      return c.id !== card.id && c.pageId === card.pageId
    })

    otherCardsOnPage.forEach(other => {
      const { position } = other
      if (position > source && position <= destination) --other.position
      if (position < source && position >= destination) ++other.position
    })

    card.position = destination
  })
}
